import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useLayoutOrder, ResetOrderButton } from "@/components/Sortable";
import { STATUS_META, STATUS_ORDER } from "@/lib/pipeline";
import { Sparkles, LayoutDashboard, Users, UploadCloud, LogOut, SlidersHorizontal, Briefcase, ChevronDown, Circle, Tag, ShieldCheck, GripVertical, UserSearch } from "lucide-react";

const links = [
  { to: "/company", icon: LayoutDashboard, label: "Prehľad", end: true, id: "side-dashboard" },
  { to: "/company/upload", icon: UploadCloud, label: "Nahrať CV", id: "side-upload" },
  { to: "/company/jobs", icon: Briefcase, label: "Ponuky", id: "side-jobs" },
  { to: "/company/search", icon: UserSearch, label: "Hľadať kandidáta", short: "Hľadať", id: "side-search" },
  { to: "/company/database", icon: Users, label: "Databáza kandidátov", short: "Kandidáti", id: "side-database", areas: true },
  { to: "/company/gdpr", icon: ShieldCheck, label: "GDPR / Súhlasy", short: "GDPR", id: "side-gdpr" },
  { to: "/company/settings", icon: SlidersHorizontal, label: "Nastavenia", id: "side-settings" },
];
const SIDEBAR_IDS = links.map((l) => l.id);
const linkById = Object.fromEntries(links.map((l) => [l.id, l]));

function SidebarRow({ id, children }) {
  const controls = useDragControls();
  return (
    <Reorder.Item as="div" value={id} dragListener={false} dragControls={controls}
      whileDrag={{ scale: 1.02, zIndex: 30 }} className="relative group">
      <div data-testid={`drag-${id}`} onPointerDown={(e) => controls.start(e)}
        title="Potiahnutím zmeníte poradie"
        className="absolute right-1 top-1/2 -translate-y-1/2 z-10 flex h-6 w-5 items-center justify-center text-slate-300 hover:text-slate-500 opacity-50 group-hover:opacity-100 cursor-grab active:cursor-grabbing touch-none select-none">
        <GripVertical size={15} />
      </div>
      {children}
    </Reorder.Item>
  );
}

export function CompanyShell({ children, wide = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const onDatabase = loc.pathname.startsWith("/company/database");
  const [dbOpen, setDbOpen] = useState(onDatabase);
  const [ov, setOv] = useState({ status_counts: {}, areas: [] });
  const [sub, setSub] = useState({ status: true, area: false });
  const { order, setOrder, reset } = useLayoutOrder("sidebar", SIDEBAR_IDS);

  useEffect(() => { if (onDatabase) setDbOpen(true); }, [onDatabase]);
  useEffect(() => {
    const f = () => api.get("/company/db/overview").then((r) => setOv(r.data)).catch(() => {});
    f();
    window.addEventListener("db-changed", f);
    return () => window.removeEventListener("db-changed", f);
  }, [loc.pathname, loc.search]);

  const qp = new URLSearchParams(loc.search);
  const onList = loc.pathname === "/company/database/list";
  const go = (k, v) => nav(`/company/database/list?${k}=${encodeURIComponent(v)}`);

  const renderLink = (l) => (
    l.areas ? (
      <div>
        <button data-testid={l.id} onClick={() => { setDbOpen((o) => !o); nav(l.to); }}
          className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 pr-8 font-medium transition-colors ${onDatabase ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"}`}>
          <l.icon size={19} /> <span className="flex-1 text-left">{l.label}</span>
          <ChevronDown size={16} className={`transition-transform ${dbOpen ? "rotate-180" : ""}`} />
        </button>
        {dbOpen && (
          <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-0.5" data-testid="side-areas">
            <SubGroup label="Stav" open={sub.status} onToggle={() => setSub((x) => ({ ...x, status: !x.status }))} testId="side-group-status">
              {STATUS_ORDER.map((k) => (
                <SubItem key={k} active={onList && qp.get("status") === k} onClick={() => go("status", k)}
                  icon={Circle} label={STATUS_META[k].l} count={ov.status_counts[k] || 0} testId={`side-status-${k}`} />
              ))}
            </SubGroup>
            <SubGroup label="Oblasť" open={sub.area} onToggle={() => setSub((x) => ({ ...x, area: !x.area }))} testId="side-group-area">
              {ov.areas.map((a) => (
                <SubItem key={a.area} active={onList && qp.get("area") === a.area} onClick={() => go("area", a.area)}
                  icon={Tag} label={a.area} count={a.count} testId={`side-area-${a.area}`} />
              ))}
            </SubGroup>
          </div>
        )}
      </div>
    ) : (
      <NavLink to={l.to} end={l.end} data-testid={l.id}
        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-3 pr-8 font-medium transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"}`}>
        <l.icon size={19} /> {l.label}
      </NavLink>
    )
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white p-6 fixed h-screen">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-blue-700 flex items-center justify-center"><Sparkles className="text-white" size={18} /></div>
          <span className="font-head font-bold tracking-tight">Profeeluj<span className="text-blue-700"> AI</span></span>
        </div>
        <Reorder.Group as="nav" axis="y" values={order} onReorder={setOrder}
          className="mt-8 space-y-1 overflow-y-auto flex-1 -mr-2 pr-2" data-testid="sidebar-nav">
          {order.map((id) => {
            const l = linkById[id];
            if (!l) return null;
            return <SidebarRow key={id} id={id}>{renderLink(l)}</SidebarRow>;
          })}
        </Reorder.Group>
        <div className="mt-3 mb-1"><ResetOrderButton onReset={reset} testId="reset-sidebar-btn" label="Obnoviť poradie menu" /></div>
        <div className="mt-2">
          <div className="text-sm text-slate-500 mb-2 px-1">{user?.company_name || user?.name}</div>
          <button data-testid="company-logout-btn" onClick={() => { logout(); nav("/"); }}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-medium px-1 transition-colors">
            <LogOut size={16} /> Odhlásiť sa
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 md:ml-64">
        <div className="md:hidden sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200 flex items-center gap-2 px-5 py-3">
          <div className="h-8 w-8 rounded-lg bg-blue-700 flex items-center justify-center"><Sparkles className="text-white" size={16} /></div>
          <span className="font-head font-bold">Profeeluj AI</span>
          <button onClick={() => { logout(); nav("/"); }} className="ml-auto text-slate-500"><LogOut size={18} /></button>
        </div>
        <main className={`p-6 md:p-10 ${wide ? "max-w-[1600px]" : "max-w-6xl"} mx-auto pb-28 md:pb-10`}>{children}</main>
        <MobileTabs />
      </div>
    </div>
  );
}

function SubItem({ active, onClick, icon: Icon, label, count, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${active ? "bg-blue-50 text-blue-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}>
      <Icon size={15} className="flex-shrink-0" />
      <span className="flex-1 text-left truncate">{label}</span>
      <span className={`text-[11px] px-1.5 rounded-full ${active ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{count}</span>
    </button>
  );
}

function SubGroup({ label, open, onToggle, testId, children }) {
  return (
    <div>
      <button data-testid={testId} onClick={onToggle}
        className="w-full flex items-center justify-between rounded-lg px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400 hover:text-violet-700">
        {label} <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="space-y-0.5 max-h-72 overflow-y-auto">{children}</div>}
    </div>
  );
}

function MobileTabs() {
  const mobileLinks = links.filter((l) => l.id !== "side-upload" && l.id !== "side-settings" && l.id !== "side-gdpr");
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 backdrop-blur-xl bg-white/85 border-t border-slate-200 flex justify-around py-2">
      {mobileLinks.map((l) => (
        <NavLink key={l.to} to={l.to} end={l.end}
          className={({ isActive }) => `flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl text-[10px] font-semibold transition-colors ${isActive ? "text-blue-700" : "text-slate-400"}`}>
          <l.icon size={20} /> {l.short || l.label}
        </NavLink>
      ))}
    </nav>
  );
}
